import { BuildingOfficeIcon, ShieldCheckIcon, ChartBarSquareIcon, ClockIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'

interface Props {
  tenants: any[]
  selectedTenantId?: string
  onTenantSelect: (tenantId?: string) => void
}

/**
 * Empty state shown to global admins on the Command Center until a tenant is
 * picked. Phase 12: tenant selection lives in the sidebar TenantContextBlock,
 * so we no longer render an inline selector here — we just point the user at
 * the sidebar.
 */
export default function DashboardEmptyState({ tenants }: Props) {
  return (
    <div className="max-w-7xl mx-auto">
      {/* Sub-header. Page title lives in the app-bar breadcrumb. */}
      <div className="mb-6">
        <p className="text-sm text-slate-400">
          Monitor Microsoft 365 configuration compliance and security drift.
        </p>
      </div>

      {/* Empty State Card */}
      <div className="card overflow-hidden relative">
        <div className="px-8 py-12 relative z-10">
          <div className="text-center max-w-2xl mx-auto">
            {/* Icon */}
            <div className="mx-auto flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-500 rounded-full mb-6 shadow-glow-cyan">
              <BuildingOfficeIcon className="w-8 h-8 text-white" />
            </div>

            {/* Title and Description */}
            <h2 className="card-title text-2xl mb-4 justify-center">
              Select a Tenant to View Command Center
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Use the tenant switcher in the sidebar to choose a tenant and view its security metrics and compliance status.
            </p>

            {/* Features List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <FeatureCard
                icon={<ChartBarSquareIcon className="w-5 h-5 text-emerald-400" />}
                tone="bg-emerald-500/15 border-emerald-500/30"
                title="Real-time Security Metrics"
                description="Active drifts, critical issues, and compliance scores"
              />
              <FeatureCard
                icon={<ShieldCheckIcon className="w-5 h-5 text-primary-400" />}
                tone="bg-primary-500/15 border-primary-500/30"
                title="Configuration Drift Analysis"
                description="Track changes and policy violations across M365"
              />
              <FeatureCard
                icon={<ClockIcon className="w-5 h-5 text-amber-400" />}
                tone="bg-amber-500/15 border-amber-500/30"
                title="Recent Activity Tracking"
                description="Monitor scan jobs and drift detection events"
              />
              <FeatureCard
                icon={<ChartBarSquareIcon className="w-5 h-5 text-secondaryBlue-300" />}
                tone="bg-secondaryBlue-300/15 border-secondaryBlue-300/30"
                title="Compliance Scoring"
                description="Overall security posture and framework alignment"
              />
            </div>

            {/* Call to Action */}
            <div className="bg-dark-900 rounded-xl p-6 border border-dark-700">
              <p className="text-sm text-slate-400 mb-2">
                You have access to{' '}
                <span className="font-medium text-text-primary">{tenants.length}</span>{' '}
                tenant{tenants.length === 1 ? '' : 's'}
              </p>
              <p className="text-sm text-primary-400 font-medium flex items-center justify-center gap-2">
                <ArrowLeftIcon className="w-4 h-4" />
                Pick one from the sidebar to get started
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FeatureCard({
  icon,
  tone,
  title,
  description,
}: {
  icon: React.ReactNode
  tone: string
  title: string
  description: string
}) {
  return (
    <div className="flex items-start space-x-3 p-4 rounded-xl bg-dark-900/50 border border-dark-700">
      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border ${tone}`}>
        {icon}
      </div>
      <div className="text-left">
        <h3 className="font-medium text-text-primary">{title}</h3>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
    </div>
  )
}
